import { useCallback, useEffect, useRef, useState } from 'react';
import { getAudioContext } from '@/audio/context';
import { detectPitch, nearestNote } from '@/core/dsp/pitch';

export type TunerStatus = 'idle' | 'starting' | 'listening' | 'denied' | 'unavailable';

export interface TunerReading {
  frequency: number;
  midi: number;
  cents: number;
}

const BUFFER = 4096;
const INTERVAL_MS = 60;
/** Readings kept for the median, which steadies the needle without lag. */
const HISTORY = 5;

/**
 * Microphone pitch tracking for the tuner (F9.2). The mic is opened only after the user presses
 * Start, analyzed on this device with an AnalyserNode (nothing is recorded or sent anywhere),
 * and released on Stop or when the page closes.
 */
export function useTuner() {
  const [status, setStatus] = useState<TunerStatus>('idle');
  const [reading, setReading] = useState<TunerReading | null>(null);
  const session = useRef<{ stop: () => void } | null>(null);

  const stop = useCallback(() => {
    session.current?.stop();
    session.current = null;
    setReading(null);
    setStatus('idle');
  }, []);

  const start = useCallback(async () => {
    if (session.current) return;
    // Missing on insecure origins and in some embedded browsers, despite the DOM typings.
    if (!('mediaDevices' in navigator)) {
      setStatus('unavailable');
      return;
    }
    setStatus('starting');
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
      });
    } catch (error) {
      setStatus(
        error instanceof DOMException && error.name === 'NotAllowedError'
          ? 'denied'
          : 'unavailable',
      );
      return;
    }
    const ctx = getAudioContext();
    await ctx.resume();
    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = BUFFER;
    source.connect(analyser);
    const buffer = new Float32Array(BUFFER);
    const history: number[] = [];
    const timer = setInterval(() => {
      analyser.getFloatTimeDomainData(buffer);
      const pitch = detectPitch(buffer, ctx.sampleRate);
      if (!pitch || pitch.clarity < 0.85) {
        if (history.length) history.shift();
        if (!history.length) setReading(null);
        return;
      }
      history.push(pitch.frequency);
      if (history.length > HISTORY) history.shift();
      const sorted = [...history].sort((a, b) => a - b);
      const frequency = sorted[sorted.length >> 1] ?? pitch.frequency;
      setReading({ frequency, ...nearestNote(frequency) });
    }, INTERVAL_MS);
    session.current = {
      stop: () => {
        clearInterval(timer);
        source.disconnect();
        for (const track of stream.getTracks()) track.stop();
      },
    };
    setStatus('listening');
  }, []);

  useEffect(
    () => () => {
      session.current?.stop();
      session.current = null;
    },
    [],
  );

  return { status, reading, start, stop };
}
